import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import { getSectionForPageByIdAndType } from '@glowforge/ui-common/models';
import { Markdown } from '@glowforge/ui-common/components';
import { formatPrice } from '../../util/formatters';
import { getSectionById } from '../../util/contentful-data';
import { productPropType } from '../../prop-types/storefront';
import { reviewsPropType } from '../../prop-types/yotpo';
import ImageCarousel from '../image-carousel';
import AffirmAsLowAsDisplay from '../affirm/affirm-as-low-as-display';
import GlowforgeProductJsonLd from './glowforge-product-json-ld';

import './glowforge-product.less';

const DISPLAY_CLASSES = ['Left Image Card', 'Right Image Card'];

class GlowforgeProduct extends React.Component {
  shouldComponentUpdate(nextProps) {
    const {
      imageSectionId: nextImageSectionId,
      product: nextProduct,
      showReferralInfo: nextShowReferralInfo,
    } = nextProps;
    const { imageSectionId, product, showReferralInfo } = this.props;

    const nextProductId = _.get(nextProduct, 'id');
    const productId = _.get(product, 'id');

    return (
      imageSectionId !== nextImageSectionId
      || productId !== nextProductId
      || showReferralInfo !== nextShowReferralInfo
    );
  }

  render() {
    const {
      sections,
      imageSectionId,
      buttonId,
      product,
      showDeliveryDate,
      showTitle,
      showAffirm,
      showReferralInfo,
      reviews,
      jsonLdTemplateId,
    } = this.props;
    const imageSection = getSectionForPageByIdAndType(
      { sections },
      imageSectionId,
      'textAndImageSection',
    );
    const button = getSectionForPageByIdAndType(
      { sections },
      buttonId,
      'buttonLink',
    );
    const jsonLdTemplateSection = getSectionById(
      { fields: { sections } },
      jsonLdTemplateId,
    );
    const jsonLdTemplate = _.get(jsonLdTemplateSection, 'fields.text', '');

    const deliveryDate = product.formattedDeliveryDate && showDeliveryDate ? (
      <span className='deliveryBadge'>{product.formattedDeliveryDate}</span>
    ) : null;

    const title = (imageSection.title && !showTitle)
      // eslint-disable-next-line react/jsx-one-expression-per-line
      && <h2 className='h2'>{imageSection.title} {deliveryDate}</h2>;
    const subTitle = (imageSection.subTitle && !showTitle)
      && <h3 className='subTitle'>{imageSection.subTitle}</h3>;
    const displayClass = DISPLAY_CLASSES.includes(imageSection.displayType)
      ? _.camelCase(imageSection.displayType)
      : 'leftImageCard';

    // Render slides
    // Slides are square
    const imageHeight = 800;
    const images = _.map(imageSection.images, (image) => ({
      src: `${_.get(image, 'fields.file.url')}?w=${imageHeight}`,
      alt: _.get(image, 'fields.title'),
      description: _.get(image, 'fields.description'),
    }));

    const thumbnailHeight = 50;
    const thumbnails = _.map(imageSection.images, (image) => ({
      src: `${_.get(image, 'fields.file.url')}?w=${thumbnailHeight}`,
      alt: _.get(image, 'fields.title'),
    }));

    // convert to getWidget(button, modify button url) // buttonLink
    const buttonObj = button ? (
      <a
        href={`${button.url}?id=${product.id}`}
        className={`buyNow button ${button.type} ${button.size}`}>
        {button.text}
      </a>
    ) : null;

    const classes = classnames(
      'GlowforgeProduct',
      imageSectionId,
      displayClass,
      { hasAffirm: showAffirm },
    );

    let price = formatPrice(product.price);
    let totalPrice = product.price;
    if (showReferralInfo && product.referral?.discount < 0) {
      totalPrice = product.price + product.referral.discount;
      // We base affirm price off of displayed price (so be sure to include
      // referral)
      price = [
        <div className='basePrice'>{price}</div>,
        <div className='discountPrice'>
          {formatPrice(totalPrice)}
          <span className='withReferral'> with referral</span>
        </div>,
      ];
    }

    const affirm = showAffirm ? (
      <AffirmAsLowAsDisplay
        amount={totalPrice}
        pageType='product'
        productName={product.name} />
    ) : null;

    return (
      <div className='contentWide fullWidth'>
        <GlowforgeProductJsonLd
          jsonLdTemplate={jsonLdTemplate}
          product={product}
          reviews={reviews} />
        <div className={classes}>
          {/* Header above image on mobile */}
          <div className='mobileSectionHeader'>
            {title}
            {subTitle}
          </div>
          <div className='imgAndDesc'>
            <ImageCarousel
              key={imageSectionId}
              images={images}
              thumbnails={thumbnails}
              naturalSlideHeight={imageHeight}
              naturalSlideWidth={imageHeight}
              showIndicator={false} />
            <div className='ogImage'>
              <img src={_.get(images, '[0].src')} alt={_.get(images, '[0].alt')} />
            </div>
          </div>
          <div className='textSection'>
            <div className='textSectionContent'>
              {/* Header inline with content on desktop */}
              <div className='desktopSectionHeader'>
                {title}
                {subTitle}
              </div>
              <Markdown className='paragraph' source={imageSection.text} />
              <div className='pricing'>
                <div className='price'>{price}</div>
                {affirm}
              </div>
              <div>
                {buttonObj}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

GlowforgeProduct.propTypes = {
  imageSectionId: PropTypes.string.isRequired,
  buttonId: PropTypes.string,
  showAffirm: PropTypes.bool,
  showDeliveryDate: PropTypes.bool,
  showReferralInfo: PropTypes.bool,
  product: productPropType.isRequired,
  showTitle: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  reviews: reviewsPropType,
  jsonLdTemplateId: PropTypes.string.isRequired,
};

GlowforgeProduct.defaultProps = {
  buttonId: null,
  showDeliveryDate: false,
  showReferralInfo: false,
  showAffirm: false,
  showTitle: false,
  reviews: null,
  sections: [],
};

export default GlowforgeProduct;
